import ConditionalsText from '../conditionals/ConditionalsText';
import CatalogNumbers from '../CatalogNumbers';
import SETTING_KEYS from '../../../constants/setting-keys';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AuthContext from '../../../store/auth-context';
import RIGHTS_KEYS from '../../../constants/rights-keys';
import styles from './ImplantDetailsComponent.module.css';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { EVENT_KEYS, trackClick } from '../../../services/api/trackingService';
import FileLink from '../../files/FileLink';
import FileModal from '../../video-overview/FileModal';

const renderValue = (
    key: string,
    value: string | string[] | number | Date,
    t: any
) => {
    if (
        value === undefined ||
        value === null ||
        value === '' ||
        (Array.isArray(value) &&
            (value.length === 0 || value.every((v) => v === '')))
    ) {
        return '';
    } else if (key === 'distEndDate') {
        return (
            <ConditionalsText
                title={'Commercial Distribution End Date'}
                delimiter=":"
                text={new Date(value as string).toLocaleDateString()}
            />
        );
    } else if (key === 'maxStentLength') {
        return (
            <ConditionalsText
                title={'Maximum Stent Length'}
                delimiter=": "
                text={
                    (value as number).toString() +
                    ' ' +
                    t('implantDetails.units.mm')
                }
            />
        );
    } else if (key === 'catalogNumbers') {
        return <CatalogNumbers data={value as any[]} />;
    } else if (Array.isArray(value)) {
        return (
            <ConditionalsText
                inline
                title={key
                    .replace(/([a-z])([A-Z])/g, '$1 $2')
                    .replace(/^./, (str) => str.toUpperCase())}
                delimiter=":"
                text={value.join(', ') as string}
            />
        );
    } else if (value !== undefined && value !== '') {
        return (
            <ConditionalsText
                inline
                title={key
                    .replace(/([a-z])([A-Z])/g, '$1 $2')
                    .replace(/^./, (str) => str.toUpperCase())}
                delimiter=":"
                text={value as string}
            />
        );
    }
    return '';
};

interface IImplantDetails {
    model: string;
    otherFiles: any[];
    category: string;
    distEndDate: string;
    bodyPlacement: string;
    catalogNumbers: any[];
    maxStentLength: number;
    gmdnDefinition: string;
    drugType: string;
}

const ImplantDetailsComponent: React.FC<{
    info: IImplantDetails;
    hasToggle?: boolean;
}> = ({ info, hasToggle = false }) => {
    const { t } = useTranslation();
    const ctx = useContext(AuthContext);

    const [showDetails, setShowDetails] = useState(false);
    const toggleDetails = () => {
        if (!showDetails) {
            trackClick(EVENT_KEYS.clickViewDetails, {
                implantModel: info.model,
            });
        }
        setShowDetails(!showDetails);
    };

    const [shownFile, setShownFile] = useState<any>(null);

    let infoKeys: (keyof IImplantDetails)[] = [
        'category',
        'distEndDate',
        'bodyPlacement',
    ];

    if (ctx.orRights([RIGHTS_KEYS.advancedSearch])) {
        infoKeys.push('catalogNumbers');
    }

    if (ctx.orRights([RIGHTS_KEYS.detailedInfo])) {
        infoKeys = infoKeys.concat([
            'maxStentLength',
            'drugType',
            'gmdnDefinition',
        ]);
    }

    if (ctx.orRights([RIGHTS_KEYS.internalInfo])) {
        infoKeys.push('gmdnDefinition');
    }

    return (
        <div className={styles.wrapper}>
            {hasToggle && (
                <div className={styles.toggle}>
                    <p
                        id="toggle"
                        className={styles.toggleText}
                        onClick={toggleDetails}
                    >
                        {showDetails
                            ? t('implantDetails.toggle.hideDetails')
                            : t('implantDetails.toggle.seeDetails')}{' '}
                        {showDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </p>
                </div>
            )}
            {(!hasToggle || showDetails) && (
                <div className={styles.details}>
                    {infoKeys.map((key: keyof IImplantDetails) => {
                        return renderValue(key, info[key], t);
                    })}
                    {info.otherFiles && info.otherFiles.length > 0 && (
                        <div className={styles.otherFiles}>
                            <h3>Documents</h3>
                            {shownFile && (
                                <>
                                    <FileModal
                                        file={{
                                            ...shownFile,
                                            title: shownFile.filename,
                                        }}
                                        close={() => setShownFile(null)}
                                    />
                                </>
                            )}
                            {info.otherFiles.map((file: any) => (
                                <FileLink file={file} onClick={setShownFile} />
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ImplantDetailsComponent;
